import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NotesService } from '../notes/notes.service';
import { AppService } from '../app.service';
import { DateTime } from 'luxon';
import { UserRole } from '../users/roles.enum'
import { firstValueFrom } from 'rxjs';
import { JOB_STATUS } from './job-status.enum';
import { Formio } from '@formio/js';
import { environment } from '../../environments/environment';
import { FormsService } from '../forms/forms.service';
import { LazyLoadMeta } from 'primeng/api';
import { jobsFilterTypes } from './jobs-manager/jobs-manager.component';

@Injectable({
  providedIn: 'root'
})
export class JobsService {
  private env = environment;
  
  constructor(
    private http: HttpClient,
    private notesService: NotesService,
    private app: AppService,
    private formsService: FormsService,
  ) { }

  getJobs(user: any, cursor: number, limit: number, filterParams: jobsFilterTypes, specificJobStatus?: JOB_STATUS): Promise<any> {
    let queryParams: string[] = [];

    if (user.userProfileId && user.userProfileId > 0 && user.roles.includes(UserRole.REP)) {
      queryParams.push(`userProfileId=${user.userProfileId}`);
      queryParams.push(`cursorValue=${cursor}`);
      queryParams.push(`pageSize=${limit}`);
      queryParams.push(`filterParams=${encodeURIComponent(JSON.stringify(filterParams))}`);
    } else {
      queryParams.push(`cursorValue=${cursor}`);
      queryParams.push(`pageSize=${limit}`);
      queryParams.push(`filterParams=${encodeURIComponent(JSON.stringify(filterParams))}`);
    }

    if (specificJobStatus !== undefined && specificJobStatus !== null) {
      queryParams.push(`specificJobStatus=${specificJobStatus}`);
    }

    const query = `?${queryParams.join('&')}`;

    return this.http.get(`${this.env.baseApiUrl}/jobs${query}`)
      .toPromise()
      .then(data => {
        return data;
      });
  }

  getJobsStatusCount(user: any,filterParams:jobsFilterTypes, specificJobStatus?: JOB_STATUS): Promise<any> {
    let queryParams: string[] = [];

    if (user.userProfileId && user.userProfileId > 0 && user.roles.includes(UserRole.REP)) {
      queryParams.push(`userProfileId=${user.userProfileId}`);
      queryParams.push(`filterParams=${encodeURIComponent(JSON.stringify(filterParams))}`);
    } else {
      queryParams.push(`filterParams=${encodeURIComponent(JSON.stringify(filterParams))}`);
    }

    if (specificJobStatus !== undefined && specificJobStatus !== null) {
      queryParams.push(`specificJobStatus=${specificJobStatus}`);
    }

    const query = `?${queryParams.join('&')}`;

    return this.http.get(`${this.env.baseApiUrl}/jobs/status-count${query}`)
      .toPromise()
      .then( data => {
        return data;
      });
  }


  sendLinkEmail(jobId: string, scJobId: number): Promise<any> {
    return this.http.get(`${this.env.baseApiUrl}/jobs/send-link-email/${jobId}`)
      .toPromise()
      .then( data => {
        this.notesService.createScJobNote(scJobId,
          `Job inspection form and photo upload link emailed to inspector.`, true)
          .catch(e => console.error(`Unable to save note for inspection form and photo upload link email.\n${e}`));
        return data;
      });
  }

  getInspectionFormLink(jobId: string, scJobId: number): Promise<any> {
    return this.http.get(`${this.env.baseApiUrl}/jobs/inspection-form-link/${jobId}`)
      .toPromise()
      .then( data => {
        return data;
      });

 }

  getMongoCompletionbyScJob(scJobId: number): Promise<any> {
    return this.http.get(`${this.env.baseApiUrl}/jobs/mongo-completion/${scJobId}`)
      .toPromise()
      .then( data => {
        return data;
      });
  }



  getJobById(jobId: string): Promise<any> {
    return this.http.get(`${this.env.baseApiUrl}/jobs/${jobId}`)
      .toPromise()
      .then( data => {
        return data;
      });
  }

  getScJobByJobId(jobId: string): Promise<any> {
    return this.http.get(`${this.env.baseApiUrl}/jobs/sc-job/${jobId}`)
      .toPromise()
      .then( data => {
        return data;
      });
  }

  getScJobByJobIdJwt(jobId: string, jwt: string): Promise<any> {
    return this.http.post(`${this.env.baseApiUrl}/jobs/sc-job-jwt/${jobId}`, { token: jwt })
      .toPromise()
      .then( data => {
        return data;
      });
  }

  updateScJob(scJobId: number, data: any): Promise<any> {
    return firstValueFrom( this.http.put(`${this.env.baseApiUrl}/jobs/prisma/${scJobId}`, { ...data }) );
  }

  updateJob(jobId: string, data: any): Promise<any> {
    return firstValueFrom( this.http.put(`${this.env.baseApiUrl}/jobs/${jobId}`, { data }) );
  }

  async updateJobIfNewer(jobId: string, timestamp: Date, data: any): Promise<{ updated: boolean, job: any, jobUpdatedBy: string|null, jobUpdatedAt: Date|null }> {
    const job = await this.getJobById(jobId);
    let jobUpdatedAt: Date|null = null;
    let jobUpdatedBy: string|null = null;
    if (data['inspectionData']) {
      jobUpdatedAt = job.dataUpdatedAt ? new Date(job.dataUpdatedAt) : null;
      jobUpdatedBy = job.dataLastUpdatedBy;
    } else if (data['inspectionPhotos']) {
      jobUpdatedAt = job.photosUpdatedAt ? new Date(job.photosUpdatedAt) : null;
      jobUpdatedBy = job.photosLastUpdatedBy;
    }
    const localTimestamp = timestamp;

    if (!jobUpdatedAt || localTimestamp.toISOString() > jobUpdatedAt.toISOString()) {
      const updatedJob = await firstValueFrom( this.http.put(`${this.env.baseApiUrl}/jobs/${jobId}`, { data }) );
      return { updated: true, job: updatedJob, jobUpdatedBy, jobUpdatedAt };
    }
    return { updated: false, job, jobUpdatedBy, jobUpdatedAt };
  }

  deliverJob(jobId: number): Promise<any> {
    return firstValueFrom( this.http.get(`${this.env.baseApiUrl}/jobs/deliver-job/${jobId}`) );
  }

  postFormDataLegacy(jobId: string): Promise<any> {
    return this.http.get(`${this.env.baseApiUrl}/jobs/post-form-data-legacy/${jobId}`)
      .toPromise()
      .then( resp => {
        return resp;
      });
  }

  getUserProfile(): any {
    const userInfo = this.app.currentUser;
    console.log(userInfo);
    return userInfo;
  }

  calculateDueDate(requestDate: string, userRole: UserRole): Date {
    let dueDate: DateTime;

    switch (userRole) {
      case UserRole.REP:
        dueDate = DateTime.fromISO(requestDate).plus({ weeks: 2 });
        break;
      case UserRole.ADMIN:
      case UserRole.USER:
        dueDate = DateTime.fromISO(requestDate).plus({ days: 30 });
        break;
      default:
        throw new Error(`Invalid user role: ${userRole}`);
    }

    return dueDate.toJSDate();
  }

  async updateScheduledDate(jobId: number, scheduledDate: string): Promise<any> {
    return firstValueFrom( this.http.put(`${this.env.baseApiUrl}/jobs/prisma/${jobId}`, { scheduledDate: scheduledDate }) );
  }
  
  async getJobRequest(jobId: number): Promise<any> {
    return firstValueFrom( this.http.get(`${this.env.baseApiUrl}/jobs/prisma/${jobId}`) );
  }

  async getJobDetails(jobId: number): Promise<any> {
    return firstValueFrom( this.http.get(`${this.env.baseApiUrl}/jobs/prisma/jobDetails/${jobId}`) );
  }

  async getActiveJobRequests(): Promise<any> {
    return firstValueFrom( this.http.get(`${this.env.baseApiUrl}/jobs/prisma/activeJobRequests`) );
  }

  async updateJobStatusId(jobId: number, jobStatusId: JOB_STATUS): Promise<any> {
    return firstValueFrom( this.http.put(`${this.env.baseApiUrl}/jobs/prisma/${jobId}`, { JobStatusId: jobStatusId }) );
  }

  getDeliveredJobs(daysBack: number): Promise<any> {
    return firstValueFrom( this.http.get(`${this.env.baseApiUrl}/jobs/delivered/${daysBack}`) );
  }

  async validateJobForQc(jobId: string, data: any = {}): Promise<any> {
    try {
      const job = await this.getJobById(jobId);
      if (!job) {
        throw new Error(`Job not found: ${jobId}`);
      }
      const form: any = await this.formsService.getFormById(job.formId);
      if (!form) {
        throw new Error(`Form not found: ${job.formId}`);
      }
      let isPhotoRequirementValid: boolean;
      if (form.photosRequired) {
        isPhotoRequirementValid = job?.inspectionPhotos && job?.inspectionPhotos.length > 0;
      } else {
        isPhotoRequirementValid = true;
      }
      if (form.photosRequired && data.photos && data.photos.length > 0) {
        isPhotoRequirementValid = data.photos.length > 0;
      }
      const dummyElement = document.createElement('div');
      const formio = await Formio.createForm(dummyElement, JSON.parse(form.inspectionForm), { submission: { data }});
      const isFormValid = await formio.checkValidity(null, true);
      let fieldsWithErrors: string[] = [];
      if (!isFormValid) {
        formio.errors.forEach((error: any) => {
          fieldsWithErrors.push(error.component.label || 'Unknown Field');
        });
        return { 
          isPhotoRequirementValid,
          isFormValid: false,
          errors: formio.errors,
          fieldsWithErrors,
          isReadyForQc: isPhotoRequirementValid && isFormValid,
        };
      } else {
        return { 
          isPhotoRequirementValid,
          isFormValid: true,
          errors: [],
          fieldsWithErrors,
          isReadyForQc: isPhotoRequirementValid && isFormValid,
        };
      }
    } catch (error) {
      console.error('Error validating job for QC:', error);
      return { isPhotoRequirementValid: false, isFormValid: false, errors: [error], fieldsWithErrors: [], isReadyForQc: false };
    }
  }
}
